/**
 * This file is part of Zeus Apollon
 *
 * (c) Kaden und Partner AG
 */
import { BaseModel } from '@/models/BaseModel';
import { BaseCollection } from '@/models/BaseCollection';
import dayjs, { Dayjs } from 'dayjs';
import { ApollonConfFk } from '@/models/ApollonConfFk';
import { ZeusDokument } from '@/models/zeus/ZeusDokument';
import type { Benutzer } from './Benutzer';
import { useAuthUser } from '@/helpers/globals';
import { createModel, type MutationsObject } from '@kp/js-model';
import { Data } from './apollon_data/Data';

export enum GesuchStatus {
    AB_APPROVED = 'ab_approved',
    AB_CANCELED = 'ab_canceled',
    AB_IN_PROGRESS = 'ab_in_progress',
    AB_INCOMPLETE = 'ab_incomplete',
    AB_REJECTED = 'ab_rejected',
    AB_SUBMIT = 'ab_submit',
    APPROVED = 'approved',
    CANCELED = 'canceled',
    CLOSED = 'closed',
    IN_PROGRESS = 'in_progress',
    INCOMPLETE = 'incomplete',
    NEW = 'new',
    READY_FOR_SUBMIT = 'ready_for_submit',
    AB_READY_FOR_SUBMIT = 'ab_ready_for_submit',
    REJECTED = 'rejected',
    SUBMIT = 'submit'
};

export enum GesuchPhase {
    EINREICHUNG = 'einreichung',
    ABSCHLUSS = 'abschluss',
    FERTIG = 'fertig'
};

export class Gesuch extends BaseModel {
    id: number | null = null;
    created: Dayjs | null = null;
    ersteller_id: number | null = null;
    bearbeiter_id: number | null = null;
    nummer: string | null = null;
    status: GesuchStatus | null = null;
    gesuchs_vorlage_id: number | null = null;
    foerderkategorie_id: number | null = null;
    kanton_id: number | null = null;
    lock: Dayjs | null = null;
    lock_benutzer_id: number | null = null;

    // View fields:
    gesuchs_vorlage: string | null = null;
    foerderkategorie: string | null = null;
    foerderkategorie_apollon: string | null = null;
    kanton: string | null = null;
    eigentuemer_display_name: string | null = null;
    eigentuemer_organisation_name: string | null = null;
    eigentuemer_vorname: string | null = null;
    eigentuemer_nachname: string | null = null;
    eigentuemer_plz: string | null = null;
    eigentuemer_ort: string | null = null;
    eigentuemer_laendercode: string | null = null;
    eigentuemer_adresse: string | null = null;
    eigentuemer_email: string | null = null;
    eigentuemer_benutzer_id: string | null = null;
    unternehmer_display_name: string | null = null;
    unternehmer_organisation_name: string | null = null;
    unternehmer_vorname: string | null = null;
    unternehmer_nachname: string | null = null;
    unternehmer_plz: string | null = null;
    unternehmer_ort: string | null = null;
    unternehmer_laendercode: string | null = null;
    unternehmer_adresse: string | null = null;
    unternehmer_email: string | null = null;
    unternehmer_benutzer_id: string | null = null;
    bevollmaechtigter_display_name: string | null = null;
    bevollmaechtigter_organisation_name: string | null = null;
    bevollmaechtigter_vorname: string | null = null;
    bevollmaechtigter_nachname: string | null = null;
    bevollmaechtigter_plz: string | null = null;
    bevollmaechtigter_ort: string | null = null;
    bevollmaechtigter_laendercode: string | null = null;
    bevollmaechtigter_adresse: string | null = null;
    bevollmaechtigter_email: string | null = null;
    bevollmaechtigter_benutzer_id: string | null = null;
    phase: GesuchPhase | null = null;
    gesuch_eingereicht_datum: Dayjs | null = null;
    gesuch_erstmals_eingereicht_datum: Dayjs | null = null;
    foerderzusage_datum: Dayjs | null = null;
    projektabschluss_eingereicht_datum: Dayjs | null = null;
    projektabschluss_erstmals_eingereicht_datum: Dayjs | null = null;
    zahlung_datum: Dayjs | null = null;
    anzahl_liegenschaften: number = 0;
    gueltigkeit_zusage: Dayjs | null = null;
    gueltigkeit_zusage_in_tagen: number | null = null;

    config: ApollonConfFk | null = null;
    data: Data | null = null;
    _zeus_dokumente: ZeusDokument[] = [];

    public mutations(): MutationsObject {
        return {
            id: (id: number) => (Number(id) > 0 ? Number(id) : null),
            eigentuemer_benutzer_id: (id: number) => (Number(id) > 0 ? Number(id) : null),
            unternehmer_benutzer_id: (id: number) => (Number(id) > 0 ? Number(id) : null),
            bevollmaechtigter_benutzer_id: (id: number) => (Number(id) > 0 ? Number(id) : null),
            created: dayjs,
            modified: dayjs,
            gesuch_eingereicht_datum: dayjs,
            gesuch_erstmals_eingereicht_datum: dayjs,
            foerderzusage_datum: dayjs,
            projektabschluss_eingereicht_datum: dayjs,
            projektabschluss_erstmals_eingereicht_datum: dayjs,
            zahlung_datum: dayjs,
            gueltigkeit_zusage: dayjs,
            gueltigkeit_zusage_in_tagen: (days: number | null) => (days === null ? null : Number(days)),
            data: (val: Data | null) => createModel(Data, val || {}),
        };
    }

    getClassName(): string {
        return 'gesuch';
    }

    /**
     * Returns true if the Gesuch is actually locked for the given Benutzer.
     * @param {Benutzer} user
     */
    isLocked(user: Benutzer | null) {
        return this.lock_benutzer_id && user?.id !== this.lock_benutzer_id;
    }

    isClosed() {
        return this.status === GesuchStatus.CANCELED || this.status === GesuchStatus.AB_CANCELED || this.status === GesuchStatus.CLOSED;
    }

    isReadOnly() {
        if (useAuthUser()?.rolle === 'admin' && !this.isClosed()) {
            return false;
        }
        return (
            this.status === GesuchStatus.AB_IN_PROGRESS ||
            this.status === GesuchStatus.AB_SUBMIT ||
            this.status === GesuchStatus.CANCELED ||
            this.status === GesuchStatus.AB_CANCELED ||
            this.status === GesuchStatus.CLOSED ||
            this.status === GesuchStatus.IN_PROGRESS ||
            this.status === GesuchStatus.SUBMIT ||
            this.status === GesuchStatus.AB_APPROVED
        );
    }

    // dynamic config from zeus.apollon_fk_config.config from the load action
    // get config() {
    //     if (!this._config) {
    //         this._config = new ApollonConfFk();
    //     }
    //     return this._config;
    // }

    // set config(newConf: ApollonConfFk) {
    //     this._config = newConf;
    // }

    // dynamic config from apollon_gesuch.data from the load action
    // get data() {
    //     if (!this._data) {
    //         this._data = new Data();
    //     }
    //     return this._data;
    // }

    // set data(newConf: Data) {
    //     this._data = newConf;
    // }

    // dynamic zeus_dokumente config from the load action
    get zeus_dokumente() {
        if (!this._zeus_dokumente) {
            this._zeus_dokumente = [];
        }
        return this._zeus_dokumente;
    }

    set zeus_dokumente(newConf) {
        this._zeus_dokumente = [];
        if (newConf instanceof Array) {
            newConf.forEach((dokConf) => {
                this._zeus_dokumente.push(createModel(ZeusDokument, dokConf));
            });
        }
    }

    get rolle() {
        const bId = useAuthUser()?.id;
        if (this.eigentuemer_benutzer_id === bId) {
            return 'eigentuemer';
        }
        if (this.bevollmaechtigter_benutzer_id === bId) {
            return 'bevollmaechtigter';
        }
        if (this.unternehmer_benutzer_id === bId) {
            return 'unternehmer';
        }
        if (this.ersteller_id === bId) {
            return 'ersteller';
        }
        return null;
    }
}
export class GesuchCollection extends BaseCollection<Gesuch> {
    protected modelCls = Gesuch;

    public getModelClassName(): string {
        return 'gesuch';
    }
}
